import { IMAGE_LIMITS, type ResourceType } from '../../../shared/types/access';
import { api, errorMessage } from './api';

/** Server limit. Bigger photos are shrunk in the browser before uploading. */
const MAX_UPLOAD_BYTES = IMAGE_LIMITS.maxBytes;
const MAX_DIMENSION = 3200;

export class UploadError extends Error { }

/**
 * Shrink an image in the browser when it's over the upload limit or very large, so phone photos
 * upload quickly and never hit the 8 MB cap. The server re-encodes everything anyway.
 */
async function prepare(file: File): Promise<Blob> {
    if (!file.type.startsWith('image/')) throw new UploadError('Choose an image file.');
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        // The browser can't decode it (e.g. HEIC on some browsers): let the server decide
        if (file.size > MAX_UPLOAD_BYTES) throw new UploadError('Image too large: max 8 MB.');
        return file;
    }
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= MAX_UPLOAD_BYTES) {
        bitmap.close();
        return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) throw new UploadError("Couldn't read that image.");
    return blob;
}

/** Upload an image to a resource (contributors only). Resolves with its key. */
export async function uploadImage(type: ResourceType, shareId: string, file: File): Promise<string> {
    const body = await prepare(file);
    try {
        const { data } = await api.post<{ key: string }>(`/api/images/${type}/${shareId}`, body, {
            headers: { 'Content-Type': body.type || 'application/octet-stream' },
        });
        return data.key;
    } catch (err) {
        throw new UploadError(errorMessage(err, "Couldn't upload the image. Try again."));
    }
}
